import React, { useState } from "react";
import { apiService } from "../../services/apiService";

const display = (v) => {
  const t = v != null ? String(v).trim() : "";
  return t || "—";
};

/**
 * Confirm deletion of one master address (and its linked input rows).
 */
const DeleteMasterAddressModal = ({
  open,
  onClose,
  onSuccess,
  tenantId,
  master,
}) => {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  if (!open || !master) return null;

  const street = display(master.std_street_address);
  const cityLine = [master.std_city, master.std_state, master.std_zip]
    .map((p) => (p != null ? String(p).trim() : ""))
    .filter(Boolean)
    .join(", ");
  const inputCount = master.totalCount || master.items?.length || 0;
  const recordCount = master.recordCount || 0;

  const handleDelete = async () => {
    if (!tenantId || deleting) return;
    setDeleting(true);
    setError(null);
    try {
      const res = await apiService.deleteMasterAddress({
        tenantId,
        master: {
          std_street_address: master.std_street_address,
          std_city: master.std_city,
          std_state: master.std_state,
          std_zip: master.std_zip,
          std_country: master.std_country || "US",
        },
      });
      if (!res.success) {
        throw new Error(res.message || res.error || "Failed to delete master");
      }
      onSuccess?.(res.data);
      onClose?.();
    } catch (err) {
      setError(err.message || "Failed to delete master address");
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 p-4">
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-master-title"
      >
        <div className="px-5 py-4 border-b border-gray-200">
          <h2
            id="delete-master-title"
            className="text-lg font-semibold text-red-700"
          >
            Delete master address
          </h2>
        </div>
        <div className="px-5 py-4 space-y-3 text-sm text-gray-700">
          <p>
            You are about to delete this master address. This cannot be undone.
          </p>
          <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
            <div className="font-semibold text-gray-900">{street}</div>
            {cityLine ? (
              <div className="text-gray-600 mt-0.5">{cityLine}</div>
            ) : null}
            {master.site_name ? (
              <div className="text-xs text-gray-500 mt-1">
                Site: {master.site_name}
              </div>
            ) : null}
          </div>
          <ul className="list-disc pl-5 space-y-1 text-gray-600">
            {recordCount > 0 ? (
              <>
                <li>
                  {recordCount.toLocaleString()} inventory record
                  {recordCount === 1 ? "" : "s"} will keep the input address and
                  show as Needs master in Detailed Inventory
                </li>
                <li>
                  Those input address{inputCount === 1 ? "" : "es"} move to the
                  Needs a master queue
                </li>
              </>
            ) : (
              <li>
                {inputCount} linked input address
                {inputCount === 1 ? "" : "es"} will be removed (no inventory
                linked)
              </li>
            )}
          </ul>
          {error ? (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          ) : null}
        </div>
        <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={deleting}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting || !tenantId}
            className="px-4 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 disabled:bg-red-300"
          >
            {deleting ? "Deleting…" : "Delete master"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteMasterAddressModal;
